import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

const EVENT_TYPES = [
  "Wedding",
  "Birthday",
  "Engagement",
  "Corporate / Brand",
  "Private Party",
  "Baby Shower",
  "Anniversary",
  "Other",
];

export default function Booking() {
  const [params] = useSearchParams();
  const preselect = params.get("package");
  const [pkgs, setPkgs] = useState([]);
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    event_time: "",
    event_type: "",
    location: "",
    guest_count: 25,
    package_id: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/packages").then((r) => {
      setPkgs(r.data);
      if (preselect) setForm((f) => ({ ...f, package_id: preselect }));
    }).catch(() => {});
  }, [preselect]);

  const selectedPkg = pkgs.find((p) => p.id === form.package_id);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const canNext1 = form.package_id && date;
  const canSubmit =
    form.customer_name && form.email && form.phone && form.event_type && form.location && form.guest_count;

  const submit = async () => {
    if (!date) return;
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        guest_count: Number(form.guest_count),
        event_date: format(date, "yyyy-MM-dd"),
        package_name: selectedPkg?.name || "",
      });
      setSubmitted(data);
      toast.success("Booking request sent! We'll be in touch within 24 hours.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-32 max-w-3xl mx-auto px-6" data-testid="booking-success">
        <div className="rounded-3xl border border-primary/25 bg-primary/5 p-10 md:p-16 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center mx-auto text-primary">
            <Check size={22} />
          </div>
          <h1 className="mt-8 font-display text-4xl md:text-5xl">Cheers, {submitted.customer_name.split(" ")[0]}!</h1>
          <p className="mt-4 text-muted-foreground text-lg">
            Your booking request for {format(new Date(submitted.event_date), "MMMM d, yyyy")} has been received.
            We'll reach out within 24 hours to confirm the details.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/" className="rounded-full px-6 py-3 border border-white/15 text-sm">Back to home</Link>
            <Link to="/gallery" className="rounded-full px-6 py-3 bg-primary text-primary-foreground text-sm">View gallery</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 min-h-screen" data-testid="booking-page">
      <header className="max-w-4xl mx-auto px-6 md:px-10 pb-10">
        <div className="overline text-primary/80">Book your event</div>
        <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05]">Let's make it pink.</h1>
        <p className="mt-4 text-muted-foreground text-lg">
          Two-step booking. Choose your date and package, then tell us about your event.
        </p>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-center gap-4 mb-10">
          {[1, 2].map((n) => (
            <div key={n} className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm ${
                  step >= n ? "bg-primary border-primary text-primary-foreground" : "border-white/15 text-muted-foreground"
                }`}
              >
                {n}
              </div>
              <div className={`text-sm ${step >= n ? "text-foreground" : "text-muted-foreground"}`}>
                {n === 1 ? "Date & Package" : "Your details"}
              </div>
              {n === 1 && <div className="w-14 h-px bg-white/10 ml-2" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-8" data-testid="booking-step-1">
            <div>
              <Label className="overline">Package</Label>
              <div className="mt-4 space-y-3">
                {pkgs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => update("package_id", p.id)}
                    data-testid={`select-package-${p.id}`}
                    className={`w-full text-left rounded-xl border p-5 transition-colors ${
                      form.package_id === p.id
                        ? "border-primary bg-primary/5"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display text-xl">{p.name}</div>
                        <div className="overline text-muted-foreground mt-1">{p.duration_hours} hrs · {p.guest_count}</div>
                      </div>
                      <div className="font-display text-2xl">£{p.price}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="overline">Event date</Label>
              <div className="mt-4 rounded-xl border border-white/10 bg-card/40 p-4">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  className="rounded-md"
                  data-testid="booking-calendar"
                />
              </div>
              {date && (
                <div className="mt-4 text-sm text-muted-foreground">
                  Selected: <span className="text-foreground">{format(date, "EEEE, MMMM d, yyyy")}</span>
                </div>
              )}
              <Button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canNext1}
                className="mt-6 rounded-full px-6 py-3 bg-primary text-primary-foreground disabled:opacity-40 hover:bg-primary/90"
                data-testid="booking-next-btn"
              >
                Continue to your details →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="booking-step-2">
            <div>
              <Label htmlFor="name" className="overline">Full name</Label>
              <Input id="name" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-name" />
            </div>
            <div>
              <Label htmlFor="email" className="overline">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-email" />
            </div>
            <div>
              <Label htmlFor="phone" className="overline">Phone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-phone" />
            </div>
            <div>
              <Label htmlFor="time" className="overline">Event start time</Label>
              <Input id="time" placeholder="e.g. 6:00 PM" value={form.event_time} onChange={(e) => update("event_time", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-time" />
            </div>
            <div>
              <Label className="overline">Event type</Label>
              <Select value={form.event_type} onValueChange={(v) => update("event_type", v)}>
                <SelectTrigger className="mt-2 bg-secondary/40 border-white/10" data-testid="select-event-type">
                  <SelectValue placeholder="Choose an event type" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="guests" className="overline">Guest count</Label>
              <Input id="guests" type="number" min={1} value={form.guest_count} onChange={(e) => update("guest_count", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-guests" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="location" className="overline">Event location / venue</Label>
              <Input id="location" value={form.location} onChange={(e) => update("location", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-location" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notes" className="overline">Anything else?</Label>
              <Textarea id="notes" rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-2 bg-secondary/40 border-white/10" data-testid="input-notes" />
            </div>
            <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-4">
              <button type="button" onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:text-foreground" data-testid="booking-back-btn">
                ← Back to date
              </button>
              <div className="flex items-center gap-4">
                {selectedPkg && (
                  <div className="text-sm text-muted-foreground">
                    {selectedPkg.name} · <span className="text-foreground">£{selectedPkg.price}</span>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || loading}
                  className="rounded-full px-7 py-3 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                  data-testid="booking-submit-btn"
                >
                  {loading ? "Sending..." : "Submit booking request"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
